import { Fragment } from "react";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { translateNormalizedRole } from "@utils/roles";
import { useTranslation } from "react-i18next";
import type { MovementServiceStatusDetails } from "../types";

interface DetailsMovementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  statusDetails?: MovementServiceStatusDetails[];
}

const serviceVesselCall = "bff-vessel-call";
const LANG_BASE = "vessel-call.movement-services.preview.details";
const TEST_ID_BASE = "movement-service-details";

const DetailsMovementDrawer = ({ isOpen, onClose, statusDetails }: DetailsMovementDrawerProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const hasStatusDetailsContent = (item: MovementServiceStatusDetails) =>
    Boolean(
      item.rejectionReason ||
        item.internalNotes ||
        item.externalNotes ||
        item.comments ||
        (Array.isArray(item.rejectionReasonAttachments) &&
          item.rejectionReasonAttachments.length > 0) ||
        (Array.isArray(item.internalAttachments) && item.internalAttachments.length > 0) ||
        (Array.isArray(item.externalAttachments) && item.externalAttachments.length > 0) ||
        (Array.isArray(item.berthPlan) && item.berthPlan.length > 0) ||
        (Array.isArray(item.unberthPlan) && item.unberthPlan.length > 0),
    );

  const getSubmittedByName = (item: MovementServiceStatusDetails) =>
    item.submittedByFullName?.trim() ||
    item.submittedByPreferredUsername?.trim() ||
    item.submittedBy?.trim() ||
    "";

  const getSubmittedByRole = (item: MovementServiceStatusDetails) =>
    translateNormalizedRole(t, item.submittedByRole);

  const getCommentsFieldKey = (item: MovementServiceStatusDetails) =>
    item.status?.includes("CANCELLED") ? "cancel-reason" : "comments";

  const getCommentsLabel = (item: MovementServiceStatusDetails) =>
    item.status?.includes("CANCELLED")
      ? t(`${LANG_BASE}.cancel-reason`)
      : t("vessel-call.movement-services.comments");

  const renderAttachments = (
    attachments: MovementServiceStatusDetails["externalAttachments"],
    field: string,
    label: string,
    statusIndex: number,
  ) =>
    Array.isArray(attachments) &&
    attachments.map((attachment, attachmentIndex) => (
      <AttachmentsComponent
        key={`${field}-${statusIndex}-${attachmentIndex}`}
        data-testid={`${TEST_ID_BASE}-${field}`}
        documentName={attachment.originalFileName || "-"}
        onDownload={() => handleDownload("document", serviceVesselCall, attachment.url || "-")}
        onPreview={() => handlePreview("document", serviceVesselCall, attachment.url || "-")}
        langBase={LANG_BASE}
        documentType={t(`${LANG_BASE}.${label}`)}
        hideDocumentType={attachmentIndex > 0}
      />
    ));

  // render only last decision, but leaving the logic for
  // multi in case we have to change it in the future
  const itemsToRender = statusDetails?.[0] ? [statusDetails[0]] : [];

  return (
    <div data-testid="details-movement-service" className="max-w-sm">
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onClose}
        title={t(`${LANG_BASE}.title`)}
        submitLabel={t("common.ok")}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          {itemsToRender?.map((item, idx) => (
            <Fragment key={`status-detail-${idx}`}>
              {item.rejectionReason && (
                <div className="col-span-full flex flex-col gap-2" key={`rejection-reason-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.rejection-reason`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-rejection-reason`}
                  >
                    {item.rejectionReason}
                  </div>
                </div>
              )}
              {renderAttachments(
                item.rejectionReasonAttachments,
                "rejection-reason-attachment",
                "rejection-attachment",
                idx,
              )}
              {item.externalNotes && (
                <div className="col-span-full flex flex-col gap-2" key={`notes-to-agent-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.notes-to-agent`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-notes-to-agent`}
                  >
                    {item.externalNotes}
                  </div>
                </div>
              )}
              {item.comments && (
                <div
                  className="col-span-full flex flex-col gap-2"
                  key={`${getCommentsFieldKey(item)}-${idx}`}
                >
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {getCommentsLabel(item)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-${getCommentsFieldKey(item)}`}
                  >
                    {item.comments}
                  </div>
                </div>
              )}
              {renderAttachments(item.externalAttachments, "attachments", "attachments", idx)}
              {item.internalNotes && (
                <div className="col-span-full flex flex-col gap-2" key={`internal-notes-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.internal-notes`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-internal-notes`}
                  >
                    {item.internalNotes}
                  </div>
                </div>
              )}
              {renderAttachments(
                item.internalAttachments,
                "internal-attachment",
                "internal-attachment",
                idx,
              )}
              {renderAttachments(item.berthPlan, "berth-plan-attachment", "berth-plan", idx)}
              {renderAttachments(item.unberthPlan, "unberth-plan-attachment", "unberth-plan", idx)}
              {hasStatusDetailsContent(item) && (
                <div>
                  <p className="text-xs text-gray-500">
                    <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
                    {getSubmittedByName(item) || getSubmittedByRole(item) ? (
                      <>
                        {getSubmittedByName(item) && (
                          <span className="text-gray-800">{getSubmittedByName(item)}</span>
                        )}
                        {getSubmittedByName(item) && getSubmittedByRole(item) ? " / " : null}
                        {getSubmittedByRole(item)}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {item.timestamp ? formatDateTime(item.timestamp) : "-"}
                  </p>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </SideDrawer>
    </div>
  );
};
export default DetailsMovementDrawer;
